import {
    Box,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, {
    ReactElement,
    useCallback,
    useContext,
    useLayoutEffect,
} from 'react';
import {
    AppFormikSelect,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import ReworkModal, { inferDocumentReworkModal } from './ReworkModal';
import mergeRefs from 'merge-refs';
import { RawDocument } from '../../listing-packets/components/VirtualizedTable';
import { useFormikContext } from 'formik';
import {
    FormikPropsFixPacketValues,
    NotExpectedMeta,
} from '../NotExpectedPage';
import { fileValidation } from 'shared/helpers/FileValidation';
import { useUploadPageState } from 'shared/hooks/UploadPage';
import { SignedReplaceDocTester } from 'shared/hooks/APITester';
import { AzureStorage } from 'shared/helpers/AzureStorage';
import { ManagePacketService } from 'services';
import { useNavigate } from 'react-router-dom';
import { Utils } from 'shared/helpers';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import { PDFDocument } from 'pdf-lib';
import FileSaver from 'file-saver';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

const Footer: React.FC<SinglePacketDocumentViewerFooter> = React.forwardRef(
    (props, ref): ReactElement => {
        const navigate = useNavigate();
        const { getFieldMeta, setFieldValue } =
            useFormikContext<FormikPropsFixPacketValues>();
        /**
         * Ref for the file input element.
         * @type {React.RefObject<HTMLInputElement>}
         */
        const fileRef = React.useRef<HTMLInputElement>(null);
        const theme = useTheme();
        const NotExpectedMetaProps = useContext(NotExpectedMeta);
        const match = useMediaQuery(theme.breakpoints.up('md'));
        const matchxs = useMediaQuery(theme.breakpoints.down('sm'));
        const [categoryData, setCategoryData] = React.useState<string[]>([]);
        const [state, setState] = useUploadPageState();
        const {
            onClose,
            onBack,
            onModalChanged,
            innerModalDocumentReworkRef,
            isDocumentSuccess,
            categoryMenuName,
            categoryDefaultValue,
            documentPlaceholder,
            onZoomChanged,
            canCategorize,
            documentRef,
            isFullScreen,
            setFullScreen,
        } = props;
        const isCategorySelected: category = React.useMemo(() => {
            const helper = getFieldMeta(categoryMenuName);
            return helper.value as category;
        }, [categoryMenuName, getFieldMeta]);

        const [isOpenDropdown, setOpenDropdown] =
            React.useState<boolean>(false);
        const [document, setDocument] = React.useState<RawDocument>();
        const [clientId, setClientId] = React.useState<string>('');
        const [packetId, setPacketId] = React.useState<string>('');
        const [zoomPx, setZoomPx] = React.useState<number>(20);
        const [, setBinNumber] = React.useState<number>(0);
        const [isSubmitFromUpload, SetSubmitFromUpload] =
            React.useState<boolean>(false);
        const [isReworkModalActive, setReworkModal] =
            React.useState<boolean>(false);
        const [, setReplaceDoc] = React.useState<boolean>(false);
        const [replaceDocumentPlaceholder, setReplaceDocumentPlaceholder] =
            React.useState<string | null>(null);
        const reworkModalRef = React.useRef<inferDocumentReworkModal | null>(
            null
        );

        /**
         * handle action to trigger rework modal
         */
        // const actionOnSaveChangesToOpenModal = React.useCallback(() => {
        //     reworkModalRef.current?.setOpen();
        // }, [reworkModalRef]);
        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * check modal rework is already active with no selection dropdown opened
                 */
                isModalFooterActive: () =>
                    isReworkModalActive ||
                    isOpenDropdown ||
                    state.uploadProgress,
                /**
                 * set default document data
                 * @param {RawDocument} _document - document data
                 * @param {RawDocument} _clientId - clientId from packet data
                 * @param {RawDocument} _packetId - packetId from actions in packet data
                 */
                setDefaultDocumentData: (
                    _document,
                    _clientId,
                    _packetId,
                    _binNumber,
                    _categoryData
                ) => {
                    setDocument(_document);
                    setClientId(_clientId);
                    setPacketId(_packetId);
                    setBinNumber(_binNumber);
                    setCategoryData(_categoryData);
                },
            }),
            [isOpenDropdown, isReworkModalActive, state.uploadProgress]
        );
        /**
         * Handles the file upload event.
         *
         * @param {ChangeEvent<HTMLInputElement>} event - The file upload event.
         */
        const handleFileUpload = useCallback(
            async (
                event: React.ChangeEvent<HTMLInputElement>
            ): Promise<any> => {
                const { files } = event.target;
                if (files && files.length > 0) {
                    const fileArray = Array.from(files);
                    let blobData:
                        | {
                              blobdata: Uint8Array;
                              name: string;
                          }[]
                        | null = null;
                    const validation = await fileValidation(
                        event,
                        (data) => {
                            blobData = data;
                        },
                        2 * 1024 * 1024 * 1024,
                        1,
                        'documents'
                    )
                        .then((bool) => bool)
                        .catch(() => false);
                    if (validation && blobData) {
                        await SignedReplaceDocTester(
                            [fileArray[0].name],
                            clientId,
                            null,
                            packetId
                        )
                            .then(({ url, fileName }) => {
                                if (categoryDefaultValue !== 'general') {
                                    setFieldValue(categoryMenuName, '');
                                }

                                setState((prevState) => ({
                                    ...prevState,
                                    fileBlob: [
                                        {
                                            url,
                                            fileName,
                                            blobdata: blobData,
                                            name: fileArray[0].name,
                                            progress: 0,
                                        },
                                    ],
                                    uploadProgress: true,
                                    fileData: fileArray,
                                    validateFile: true,
                                    isChoosingFile: false,
                                }));

                                setReplaceDoc(true);
                                setReplaceDocumentPlaceholder(
                                    fileArray[0].name
                                );
                            })
                            .catch((error) => {
                                Utils.handleErrorResponse(error);
                                setState((prevState) => ({
                                    ...prevState,
                                    fileBlob: [
                                        {
                                            blobdata: blobData,
                                            name: fileArray[0].name,
                                            progress: -1,
                                        },
                                    ],
                                    fileData: fileArray,
                                    validateFile: true,
                                    uploadProgress: false,
                                    isChoosingFile: false,
                                }));
                                setReplaceDoc(false);
                                setReplaceDocumentPlaceholder(null);
                            });
                    } else {
                        setReplaceDoc(false);
                        setReplaceDocumentPlaceholder(null);
                    }
                } else {
                    setState((prevState) => ({
                        ...prevState,
                        isChoosingFile: false,
                    }));
                }
                // eslint-disable-next-line no-param-reassign
                event.target.value = '';
            },
            [
                categoryDefaultValue,
                categoryMenuName,
                clientId,
                packetId,
                setFieldValue,
                setState,
            ]
        );

        // const handleClickReplaceDocument = React.useCallback(() => {
        //     fileRef.current?.click();
        //     setState((prevState) => ({
        //         ...prevState,
        //         isChoosingFile: true,
        //     }));
        // }, [setState]);

        React.useLayoutEffect(() => {
            if (
                state.isChoosingFile === false &&
                state.validateFile &&
                state.fileBlob.some((i: any) => i.progress === 0)
            ) {
                const data = state.fileBlob.find((i: any) => i.progress === 0);
                let statusText = '';
                if (data) {
                    AzureStorage(
                        data.blobdata,
                        data.url,
                        () => {},
                        (ev: string) => {
                            statusText = ev;
                        }
                    ).finally(() => {
                        if (statusText === 'Created') {
                            SetSubmitFromUpload(true);
                            setState({
                                ...state,
                                uploadProgress: false,
                            });
                        } else {
                            setState({
                                ...state,
                                uploadProgress: false,
                            });
                            setReplaceDoc(false);
                            setReplaceDocumentPlaceholder(null);
                        }
                    });
                }
            }
        }, [state.isChoosingFile, state.validateFile, state.fileBlob]);

        const handleClosingModal = React.useCallback(() => {
            if (isSubmitFromUpload && isCategorySelected !== 'general') {
                NotExpectedMetaProps?.setActiveBackNavigation(true);
                ManagePacketService.replaceDocument({
                    clientId,
                    packetId,
                    replaceList: [
                        {
                            replaceDocumentId: document?.actions.uuid ?? '',
                            file: state.fileBlob[0].fileName,
                            category: isCategorySelected,
                            placeholder: state.fileBlob[0].name,
                        },
                    ],
                })
                    .then((response) => {
                        Utils.handleSuccessResponse(response);
                        navigate(-1);
                    })
                    .catch((error) => {
                        NotExpectedMetaProps?.setActiveBackNavigation(false);
                        Utils.handleErrorResponse(error);
                    });
            } else if (typeof onClose === 'function') {
                onClose();
            }
        }, [
            NotExpectedMetaProps,
            clientId,
            document?.actions.uuid,
            isCategorySelected,
            isSubmitFromUpload,
            navigate,
            onClose,
            packetId,
            state.fileBlob,
        ]);
        const actionFullScreenForLeftViewer = React.useCallback(() => {
            if (typeof setFullScreen === 'function') {
                setFullScreen(!isFullScreen);
            }
        }, [isFullScreen, setFullScreen]);

        const disabledSaveButton = React.useMemo(() => {
            // const field = getFieldMeta(categoryMenuName);
            // if (typeof field.error === 'string') {
            //     return true;
            // }
            if (state.uploadProgress === true) {
                return true;
            }
            return false;
        }, [categoryMenuName, getFieldMeta, state.uploadProgress]);
        const onHandleZoomOut = React.useCallback(() => {
            if (zoomPx > 0) {
                const newzoomPx = zoomPx - 5;

                setZoomPx(newzoomPx);
            }
        }, [zoomPx]);
        const onHandleZoomIn = React.useCallback(() => {
            if (zoomPx < 100) {
                const newZoomPx = zoomPx + 5;

                setZoomPx(newZoomPx);
            }
        }, [zoomPx]);
        useLayoutEffect(() => {
            if (typeof onZoomChanged === 'function') {
                onZoomChanged(zoomPx);
            }
        }, [zoomPx]);
        const actionDownloadDocumentInView = React.useCallback(async () => {
            if (documentRef && documentRef.current) {
                const { current: documentProxy } = documentRef;
                if (documentProxy?.linkService) {
                    await documentProxy?.linkService.current.pdfDocument
                        ?.getData()
                        .then(async (res) => {
                            const pdfDoc = await PDFDocument.load(res);

                            const base64DataUri = await pdfDoc.saveAsBase64({
                                dataUri: true,
                            });

                            fetch(base64DataUri)
                                .then((resp) => resp.blob())
                                .then((blob) => {
                                    FileSaver.saveAs(blob, documentPlaceholder);
                                });
                        });
                }
            }
        }, [documentPlaceholder, documentRef]);
        return (
            <Box className="footer document-modal-footer">
                <input
                    type="file"
                    hidden
                    ref={fileRef}
                    accept=".pdf"
                    multiple={false}
                    onInput={handleFileUpload}
                />
                <Grid container spacing={2}>
                    <Grid
                        container
                        item
                        direction={
                            match && canCategorize ? 'row-reverse' : 'row'
                        }
                        xs={canCategorize ? 12 : 6}
                        sm={canCategorize ? 12 : 10}
                        md={canCategorize ? 8 : 10}
                        spacing={1}
                        className="task-document-modal"
                    >
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={canCategorize ? 12 : 12}
                            lg={canCategorize ? 12 : 12}
                            className="document-metadata"
                        >
                            <Stack
                                direction={
                                    !canCategorize && matchxs ? 'column' : 'row'
                                }
                                className="container-metadata"
                            >
                                {isFullScreen === false && (
                                    <Box className="filename-container">
                                        <Typography
                                            variant="h6"
                                            className="metadata-content"
                                        >
                                            File Name
                                        </Typography>
                                        <Typography
                                            data-testid="placeholder-filename"
                                            className="metadata-content"
                                        >
                                            {replaceDocumentPlaceholder !== null
                                                ? replaceDocumentPlaceholder
                                                : documentPlaceholder}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    className={
                                        !matchxs
                                            ? 'zoom-container'
                                            : 'zoom-container inline'
                                    }
                                >
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        flexGrow={1}
                                        alignItems="flex-start"
                                        justifyContent="end"
                                    >
                                        <FormControl
                                            fullWidth
                                            variant="standard"
                                            className="aggregator-zoom"
                                        >
                                            <InputLabel htmlFor="standard-adornment-amount">
                                                Zoom
                                            </InputLabel>
                                            <Input
                                                inputProps={{
                                                    'data-testid':
                                                        'test-zoom-label',
                                                    role: 'spinbutton',
                                                    'aria-label': 'zoom',
                                                    'aria-invalid':
                                                        zoomPx < 0 ||
                                                        zoomPx > 100
                                                            ? 'true'
                                                            : 'false',
                                                    'aria-valuenow': zoomPx,
                                                    'aria-valuemax': 100,
                                                    'aria-valuemin': 0,
                                                }}
                                                disableUnderline
                                                value={`${zoomPx}%`}
                                                disabled
                                                id="standard-adornment-amount"
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <IconButton
                                                            disabled={
                                                                !isDocumentSuccess
                                                            }
                                                            onClick={
                                                                onHandleZoomOut
                                                            }
                                                            //   onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            title="zoom out"
                                                        >
                                                            <ZoomOut />
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            disabled={
                                                                !isDocumentSuccess
                                                            }
                                                            onClick={
                                                                onHandleZoomIn
                                                            }
                                                            //   onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            title="zoom in"
                                                        >
                                                            <ZoomIn />
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                        <IconButton
                                            className="tooltip-button"
                                            aria-label="fullscreen"
                                            disabled={!isDocumentSuccess}
                                            onClick={
                                                actionFullScreenForLeftViewer
                                            }
                                        >
                                            {isFullScreen ? (
                                                <CloseFullscreenIcon />
                                            ) : (
                                                <OpenInFullIcon />
                                            )}
                                        </IconButton>
                                        <IconButton
                                            className="tooltip-button"
                                            aria-label="download"
                                            disabled={!isDocumentSuccess}
                                            onClick={
                                                actionDownloadDocumentInView
                                            }
                                        >
                                            <FileDownloadOutlinedIcon />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            </Stack>
                        </Grid>
                        {/* {canCategorize && (
                            // eslint-disable-next-line react/jsx-no-useless-fragment
                            <Grid item xs={12} sm={12} md={3}>
                                <Box className="task-document-modal-not-expected">
                                    <AppPrimaryButton
                                        onClick={actionOnSaveChangesToOpenModal}
                                        title="Send for Rework"
                                        className="btn-small back-button"
                                        disabled={
                                            !isDocumentSuccess ||
                                            isReplacingDocument
                                        }
                                    />
                                </Box>
                            </Grid>
                        )} */}
                    </Grid>
                    {isFullScreen === false && (
                        <Grid
                            item
                            xs={canCategorize ? 12 : 6}
                            sm={canCategorize ? 12 : 2}
                            md={canCategorize ? 4 : 2}
                            className="task-document-modal"
                        >
                            <Box className="saving-document-modal-not-expected">
                                <Grid
                                    className="saving-document-container"
                                    container
                                    spacing={2}
                                    direction={
                                        canCategorize ? 'row' : 'row-reverse'
                                    }
                                >
                                    {canCategorize && (
                                        <Grid
                                            item
                                            xs={6}
                                            className="categorise-selection"
                                        >
                                            <InputLabel id="fix-selection-label">
                                                Select a category
                                            </InputLabel>
                                            <AppFormikSelect
                                                className="selection-container"
                                                onOpen={() => {
                                                    setOpenDropdown(true);
                                                }}
                                                onClose={() => {
                                                    setOpenDropdown(false);
                                                }}
                                                name={categoryMenuName}
                                                notched={false}
                                                labelId="fix-selection-label"
                                                MenuProps={{
                                                    className:
                                                        'menu-fix-selection',
                                                }}
                                                label="Category"
                                                defaultValue={
                                                    categoryDefaultValue
                                                }
                                                items={categoryData}
                                            />
                                        </Grid>
                                    )}
                                    <Grid
                                        item
                                        xs={!canCategorize ? 12 : 6}
                                        sm={!canCategorize ? 12 : 6}
                                        className="categorise-button"
                                    >
                                        <Stack
                                            direction={
                                                matchxs ? 'column' : 'row'
                                            }
                                            spacing={2}
                                        >
                                            {canCategorize && (
                                                <AppPrimaryButton
                                                    disabled={
                                                        disabledSaveButton ||
                                                        NotExpectedMetaProps?.isActiveBackNavigation
                                                    }
                                                    title="Save changes"
                                                    className="btn-small save-button"
                                                    onClick={handleClosingModal}
                                                />
                                            )}
                                            <AppPrimaryButton
                                                disabled={disabledSaveButton}
                                                title="Close"
                                                className="btn-small back-button"
                                                onClick={onBack}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                <ReworkModal
                    document={document}
                    clientId={clientId}
                    packetId={packetId}
                    ref={mergeRefs(reworkModalRef, innerModalDocumentReworkRef)}
                    callbackOpenModal={() => {
                        setReworkModal(true);
                        // console.log('test');
                        if (typeof onModalChanged === 'function')
                            onModalChanged(true, false, false);
                    }}
                    callbackClosingModal={() => {
                        setReworkModal(false);
                        if (typeof onModalChanged === 'function')
                            onModalChanged(false, false, false);
                    }}
                />
            </Box>
        );
    }
);

export default Footer;
